import { useMutation } from '@tanstack/react-query';
import { shipmentTypesenseService } from '~/services/shipment-typesense-service';
import type { RuleImpactFilterResult } from '~/utils/ruleImpactCount/ruleCriteriaToTypesenseFilter';
import { buildBaseFilter } from '~/utils/ruleImpactCount/ruleCriteriaToTypesenseFilter';
import { DEFAULT_VALUES } from '~/utils/shipmentSearch/typesense-constants';
import type { MultiSearchResult, TypesenseDocument } from '~/types/typesense-search';
import type { TypesenseQueryParams } from '~/utils/shipmentSearch/typesense-adapters';
import { useSession } from '~/hooks/useSession';

export interface ExampleShipment {
  shipiumShipmentId: string;
  partnerShipmentId: string;
  carrierId: string;
  serviceMethodId: string;
  destination: string;
  createdDateTime: number;
}

export interface RuleImpactCountResult {
  count: number;
  carrierMethodCount?: number;
  total: number;
  exampleShipments: ExampleShipment[];
  /** True when the rule had criteria the filter couldn't translate — count is an upper bound. */
  hasUnsupportedCriteria: boolean;
}

const EXAMPLE_SHIPMENTS_LIMIT = 5;

function toExampleShipment(doc: TypesenseDocument): ExampleShipment {
  return {
    shipiumShipmentId: doc.shipium_shipment_id,
    partnerShipmentId: doc.partner_shipment_id || '',
    carrierId: doc.carrier_id_facet || '',
    serviceMethodId: doc.carrier_service_method_id_facet || '',
    destination: [doc.destination_postal_code, doc.destination_state_facet, doc.destination_country_code_facet]
      .filter(Boolean)
      .join(', '),
    createdDateTime: doc.created_date_time,
  };
}

// Per-search error from multi_search comes back as a 200 with `error` set on the offending
// sub-result. Surface it so React Query treats the mutation as failed instead of silently returning zeros.
function assertNoSubError(result: MultiSearchResult | undefined, label: string): void {
  if (!result) {
    throw new Error(`${label}: no result returned from multi_search`);
  }
  if (result.error) {
    throw new Error(`${label}: ${result.error}`);
  }
}

export function useRuleImpactCount() {
  const { sessionPartnerUrl } = useSession();
  return useMutation({
    mutationFn: async (filterResult: RuleImpactFilterResult): Promise<RuleImpactCountResult> => {
      const searches: TypesenseQueryParams[] = [
        // [0] Match: example shipments + count matching the rule's criteria.
        { q: '*', query_by: '*', filter_by: filterResult.filterBy, per_page: EXAMPLE_SHIPMENTS_LIMIT, page: 1 },
        // [1] Total: denominator for the "X of Y" stat.
        { q: '*', query_by: '*', filter_by: buildBaseFilter(), per_page: 0, page: 1 },
      ];
      if (filterResult.carrierMethodFilter) {
        // [2] Overlap: how many matching shipments already use the rule's carriers/methods.
        searches.push({
          q: '*',
          query_by: '*',
          filter_by: [filterResult.filterBy, filterResult.carrierMethodFilter].join(DEFAULT_VALUES.FILTER_OPERATOR),
          per_page: 0,
          page: 1,
        });
      }

      const { results } = await shipmentTypesenseService.multiSearch.fn(sessionPartnerUrl, searches);
      const [matchResult, totalResult, overlapResult] = results;

      assertNoSubError(matchResult, 'Match search');
      assertNoSubError(totalResult, 'Total search');
      if (filterResult.carrierMethodFilter) assertNoSubError(overlapResult, 'Overlap search');

      const exampleShipments = (matchResult.hits ?? []).map((hit) =>
        toExampleShipment(hit.document as TypesenseDocument),
      );

      return {
        count: matchResult.found ?? 0,
        carrierMethodCount: overlapResult?.found,
        total: totalResult.found ?? 0,
        exampleShipments,
        hasUnsupportedCriteria: filterResult.hasUnsupportedCriteria,
      };
    },
  });
}
